/** Pure helpers for the Straddle tab: timeframe resampling and the
 * premium/spot axis bounds. Kept out of the components for unit tests
 * and the size lint. */

import type { Bounds } from "@/components/Reports/analyticsMath";
import type { StraddlePoint } from "@/types/market";

export const TIMEFRAMES = [1, 3, 5, 15] as const;
export type Timeframe = (typeof TIMEFRAMES)[number];

/** Which optional lines are drawn (the straddle itself always is). */
export type StraddleSeriesKey = "spot" | "vwap" | "ce" | "pe";

/** The visibility set with one key flipped (immutably — React state). */
export function toggled(
  prev: ReadonlySet<StraddleSeriesKey>,
  key: StraddleSeriesKey,
): ReadonlySet<StraddleSeriesKey> {
  const next = new Set(prev);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/** Last point of each `minutes`-wide bucket, anchored on the first bar —
 * how a coarser candle's close would read, which is what StockMock's
 * 2m/3m/5m/15m buttons show. 1m returns the series untouched. */
export function resample(points: readonly StraddlePoint[], minutes: Timeframe): StraddlePoint[] {
  const head = points[0];
  if (minutes === 1 || head === undefined) return [...points];
  const anchor = new Date(head.at).getTime();
  const out: StraddlePoint[] = [];
  let bucket = Number.NaN;
  for (const p of points) {
    const b = Math.floor((new Date(p.at).getTime() - anchor) / (minutes * 60_000));
    if (b === bucket) out[out.length - 1] = p;
    else {
      out.push(p);
      bucket = b;
    }
  }
  return out;
}

/** Visible window over the session as fractions of the series (0..1),
 * driven by the brush strip under the chart (StockMock's slider). */
export interface BrushRange {
  start: number;
  end: number;
}

export const FULL_RANGE: BrushRange = { start: 0, end: 1 };

/** Narrowest window the brush allows — ~7 minutes of a full session, so
 * a drag can never collapse the chart to a sliver. */
export const MIN_RANGE_WIDTH = 0.02;

/** How close to a window edge (in fractions) counts as grabbing it. */
const HANDLE_TOLERANCE = 0.02;

export type BrushMode = "left" | "right" | "pan" | "jump";

/** What a pointer-down at `frac` means: an edge drag, a pan from inside
 * the window, or a jump from outside it. */
export function brushModeAt(frac: number, r: BrushRange): BrushMode {
  if (Math.abs(frac - r.start) <= HANDLE_TOLERANCE) return "left";
  if (Math.abs(frac - r.end) <= HANDLE_TOLERANCE) return "right";
  if (frac > r.start && frac < r.end) return "pan";
  return "jump";
}

/** The window re-centered on `frac`, width kept, clamped to [0, 1]. */
export function jumpRange(r: BrushRange, frac: number): BrushRange {
  const width = r.end - r.start;
  const start = Math.min(Math.max(frac - width / 2, 0), 1 - width);
  return { start, end: start + width };
}

/** The window after a drag to `frac`. Pan keeps the width (`offset` is
 * where inside the window the drag grabbed it); edge drags resize but
 * never past the other edge minus the minimum width. */
export function movedRange(
  mode: BrushMode,
  prev: BrushRange,
  frac: number,
  offset: number,
): BrushRange {
  if (mode === "left") {
    return { start: Math.max(Math.min(frac, prev.end - MIN_RANGE_WIDTH), 0), end: prev.end };
  }
  if (mode === "right") {
    return { start: prev.start, end: Math.min(Math.max(frac, prev.start + MIN_RANGE_WIDTH), 1) };
  }
  const width = prev.end - prev.start;
  const start = Math.min(Math.max(frac - offset, 0), 1 - width);
  return { start, end: start + width };
}

/** Halve (in) or double (out) the window, anchored to its right edge —
 * matching the old zoom buttons, which the brush now backs. */
export function zoomRange(r: BrushRange, dir: "in" | "out"): BrushRange {
  const width = r.end - r.start;
  if (dir === "in") {
    const next = Math.max(width / 2, MIN_RANGE_WIDTH);
    return { start: r.end - next, end: r.end };
  }
  const next = Math.min(width * 2, 1);
  const start = Math.max(0, r.end - next);
  return { start, end: Math.min(1, start + next) };
}

/** The window's slice of the series — always at least two points so the
 * chart keeps a line to draw. */
export function sliceRange<T>(points: readonly T[], r: BrushRange): T[] {
  const n = points.length;
  if (n <= 2) return [...points];
  let i = Math.floor(r.start * n);
  const j = Math.min(n, Math.max(Math.ceil(r.end * n), i + 2));
  i = Math.max(0, Math.min(i, j - 2));
  return points.slice(i, j);
}

/** Tight min/max with a small pad; widens a flat window so a line never
 * sits on the canvas edge. */
export function paddedBounds(values: readonly number[]): Bounds {
  if (values.length === 0) return { min: 0, max: 1 };
  const min = Math.min(...values);
  const max = Math.max(...values);
  const pad = Math.max((max - min) * 0.08, Math.abs(max) * 0.002, 0.5);
  return { min: min - pad, max: max + pad };
}

/** Bounds over the straddle line plus whichever premium-axis companions
 * are visible (VWAP / CE / PE) — they share the right-hand axis. */
export function premiumBounds(
  points: readonly StraddlePoint[],
  visible: ReadonlySet<StraddleSeriesKey>,
): Bounds {
  const values = points.flatMap((p) => [
    p.straddle,
    ...(visible.has("vwap") && p.vwap !== null ? [p.vwap] : []),
    ...(visible.has("ce") ? [p.ce] : []),
    ...(visible.has("pe") ? [p.pe] : []),
  ]);
  return paddedBounds(values);
}
